import React, { useEffect, useMemo } from "react";
import { TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useCustomParams } from "hooks/useCustomParams";
import { useAuth } from "hooks";
import ExportGLSalesReport from "./ExportGLSalesReport";

import {
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
  useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import {
  useLazyGetDdlRegionListQuery,
  useLazyGetDDLRestrictedRegionListQuery,
} from "store/services/LocationsApi/regionApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const DATE_RANGE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Custom", value: "custom" },
];

const PAYER_OPTIONS = [
  { label: "All Payers", value: "" },
  { label: "Vendor", value: "VENDOR" },
  { label: "Sender", value: "SENDER" },
  { label: "Receiver", value: "RECEIVER" },
];

const computeRangeDates = (range, customFrom, customTo) => {
  switch (range) {
    case "today":
      return {
        fromDate: moment().format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "last7days":
      return {
        fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "lastMonth":
      return {
        fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
      };
    case "custom":
      return { fromDate: customFrom || "", toDate: customTo || "" };
    default:
      return { fromDate: "", toDate: "" };
  }
};

const GLSalesReportFilter = ({ disabled = false }) => {
  const { params, appendParams } = useCustomParams();

  const timeRange = params.timeRange;
  const customFrom = params.fromDate || "";
  const customTo = params.toDate || "";

  const sourceRegion = params.sourceRegion;
  const destinationRegion = params.destinationRegion;
  const sourceBranchIds = useMemo(
    () => params.sourceBranch?.split(",") ?? [],
    [params.sourceBranch],
  );
  const destinationBranchIds = useMemo(
    () => params.destinationBranch?.split(",") ?? [],
    [params.destinationBranch],
  );

  const { viewEntity } = useAuth();
  const [triggerGetDDLBranchList, { data: branchesData }] =
    useLazyGetDDLBranchListQuery();
  const [triggerGetDDLDestinationBranchList, { data: destinationBranchesAllData }] =
    useLazyGetDDLDestinationBranchListQuery();
  const [triggerGetDDLRestrictedRegionList, { data: restrictedRegionsData }] =
    useLazyGetDDLRestrictedRegionListQuery();
  const [triggerGetDdlRegionList, { data: regionsData }] =
    useLazyGetDdlRegionListQuery();
  const [triggerGetSourceBranchesByRegion, { data: sourceBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();
  const [triggerGetDestinationBranchesByRegion, { data: destinationBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();

  const mappedBranchList = useMemo(() => {
    const arr = Array.isArray(branchesData)
      ? branchesData
      : Array.isArray(branchesData?.data)
        ? branchesData.data
        : [];
    return arr
      .filter((b) => b?._id && b?.name && b?.code)
      .map((b) => ({ label: `${b.name} - ${b.code}`, value: b._id }));
  }, [branchesData]);

  const mappedDestinationBranchList = useMemo(() => {
    const arr = Array.isArray(destinationBranchesAllData)
      ? destinationBranchesAllData
      : Array.isArray(destinationBranchesAllData?.data)
        ? destinationBranchesAllData.data
        : [];
    return arr
      .filter((b) => b?._id && b?.name && b?.code)
      .map((b) => ({ label: `${b.name} - ${b.code}`, value: b._id }));
  }, [destinationBranchesAllData]);

  const mappedRestrictedRegionList = useMemo(() => {
    const arr = Array.isArray(restrictedRegionsData)
      ? restrictedRegionsData
      : Array.isArray(restrictedRegionsData?.data)
        ? restrictedRegionsData.data
        : [];
    return arr
      .filter((r) => r?._id && r?.name)
      .map((r) => ({ label: r.name, value: r._id }));
  }, [restrictedRegionsData]);

  const mappedRegionList = useMemo(() => {
    const arr = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];
    return arr
      .filter((r) => r?._id && r?.name)
      .map((r) => ({ label: r.name, value: r._id }));
  }, [regionsData]);

  const selectedSource = useMemo(
    () => mappedBranchList.filter((b) => sourceBranchIds.includes(b.value)),
    [mappedBranchList, sourceBranchIds],
  );
  const selectedDestination = useMemo(
    () =>
      mappedDestinationBranchList.filter((b) =>
        destinationBranchIds.includes(b.value),
      ),
    [mappedDestinationBranchList, destinationBranchIds],
  );
  const selectedSourceRegion = useMemo(
    () =>
      mappedRestrictedRegionList.find((r) => r.value === sourceRegion) || null,
    [mappedRestrictedRegionList, sourceRegion],
  );
  const selectedDestinationRegion = useMemo(
    () => mappedRegionList.find((r) => r.value === destinationRegion) || null,
    [mappedRegionList, destinationRegion],
  );

  // Fee item (feeItem) options come from the List model via listService
  const { data: feeItemListData = [] } = useListListQuery({
    type: ENTITY_LIST_CONSTANTS.SHIPMENT_FEES,
    status: "Active",
  });
  const mappedFeeItemList = useMemo(
    () =>
      Array.isArray(feeItemListData)
        ? feeItemListData.map((i) => ({ label: i?.name, value: i?._id }))
        : [],
    [feeItemListData],
  );
  const selectedFeeItem = useMemo(
    () =>
      mappedFeeItemList.find(
        (i) => i.value === (params.feeItem || ""),
      ) || null,
    [mappedFeeItemList, params.feeItem],
  );

  useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDDLBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDDLDestinationBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDDLRestrictedRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      if (sourceRegion) {
        triggerGetSourceBranchesByRegion({
          regionId: sourceRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }
      if (destinationRegion) {
        triggerGetDestinationBranchesByRegion({
          regionId: destinationRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }
    }
  }, [
    viewEntity,
    sourceRegion,
    destinationRegion,
    triggerGetDDLBranchList,
    triggerGetDDLDestinationBranchList,
    triggerGetDdlRegionList,
    triggerGetDDLRestrictedRegionList,
    triggerGetSourceBranchesByRegion,
    triggerGetDestinationBranchesByRegion,
  ]);

  useEffect(() => {
    if (!timeRange) {
      const { fromDate, toDate } = computeRangeDates("last7days");
      appendParams({ timeRange: "last7days", fromDate, toDate });
    }
  }, [timeRange, appendParams]);

  const handleRangeChange = (newRange) => {
    if (newRange === "custom") {
      appendParams({ timeRange: "custom" });
      return;
    }
    const { fromDate, toDate } = computeRangeDates(newRange);
    appendParams({
      timeRange: newRange,
      fromDate,
      toDate,
    });
  };

  const filters = {
    keyword: params.search || "",
    fromDate: customFrom,
    toDate: customTo,
    serviceChargePayer: params.serviceChargePayer || "",
    sourceBranch: params.sourceBranch || "",
    destinationBranch: params.destinationBranch || "",
    sourceRegion: params.sourceRegion || "",
    destinationRegion: params.destinationRegion || "",
    feeItem: params.feeItem || "",
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TableFilter
        searchLabel="Shipment ID / GL No"
        disabled={disabled}
        initialFilters={3}
        actions={<ExportGLSalesReport filters={filters} disabled={disabled} />}
      >
        <BaseSelect
          fullWidth
          label="Date Range"
          value={timeRange || "last7days"}
          onChange={(e) => handleRangeChange(e.target.value)}
          options={DATE_RANGE_OPTIONS}
          getOptionLabel={(opt) => opt?.label || ""}
          getOptionValue={(opt) => opt?.value || ""}
          size="small"
        />

        {timeRange === "custom" && (
          <>
            <DatePicker
              label="From Date"
              value={customFrom ? moment(customFrom) : null}
              onChange={(val) =>
                appendParams({
                  fromDate: val ? val.format("YYYY-MM-DD") : "",
                })
              }
              renderInput={(p) => (
                <TextField {...p} size="small" fullWidth />
              )}
              format="MM/DD/YYYY"
              maxDate={customTo ? moment(customTo) : moment()}
              disableFuture
            />
            <DatePicker
              label="To Date"
              value={customTo ? moment(customTo) : null}
              onChange={(val) =>
                appendParams({
                  toDate: val ? val.format("YYYY-MM-DD") : "",
                })
              }
              renderInput={(p) => (
                <TextField {...p} size="small" fullWidth />
              )}
              format="MM/DD/YYYY"
              minDate={customFrom ? moment(customFrom) : undefined}
              maxDate={moment()}
              disableFuture
            />
          </>
        )}

        <BaseSelect
          fullWidth
          label="Payer"
          value={params.serviceChargePayer || ""}
          onChange={(e) =>
            appendParams({ serviceChargePayer: e.target.value })
          }
          options={PAYER_OPTIONS}
          getOptionLabel={(opt) => opt?.label || ""}
          getOptionValue={(opt) => opt?.value || ""}
          size="small"
        />
        <BaseAutocomplete
          fullWidth
          label="Fee Item"
          value={selectedFeeItem}
          options={mappedFeeItemList}
          onChange={(_, selected) =>
            appendParams({ feeItem: selected?.value || "" })
          }
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          size="small"
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Source Branch"
          value={selectedSource}
          options={mappedBranchList}
          limitTags={2}
          onChange={(_, data) =>
            appendParams({
              sourceBranch: data.map((b) => b.value).join(","),
              sourceRegion: "",
            })
          }
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          size="small"
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Destination Branch"
          value={selectedDestination}
          options={mappedDestinationBranchList}
          limitTags={2}
          onChange={(_, data) =>
            appendParams({
              destinationBranch: data.map((b) => b.value).join(","),
              destinationRegion: "",
            })
          }
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          size="small"
        />

        <BaseAutocomplete
          fullWidth
          label="Source Province"
          value={selectedSourceRegion}
          options={mappedRestrictedRegionList}
          onChange={(_, selected) =>
            appendParams({
              sourceRegion: selected?.value || "",
              sourceBranch: "",
            })
          }
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          size="small"
        />

        <BaseAutocomplete
          fullWidth
          label="Destination Province"
          value={selectedDestinationRegion}
          options={mappedRegionList}
          onChange={(_, selected) =>
            appendParams({
              destinationRegion: selected?.value || "",
              destinationBranch: "",
            })
          }
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
          size="small"
        />
      </TableFilter>
    </LocalizationProvider>
  );
};

export default GLSalesReportFilter;
